// Stok bekleyen siparişler: the Özet banner's words, and what the floor can do about a product orders
// wait for.
import { waitingFor, type MarketplaceStockSeen, type StockWaitingInbound, type StockWaitingProduct, type StockWaitingSummary } from '@wms/domain';
import { fmt } from './format';

/**
 * The Özet banner: "3 sipariş stok bekliyor — en eskisi 2 gündür: YLM-PALET-45, MUG-1, …"; null when
 * no order waits (or the server is older than the banner).
 */
export function stockWaitingBanner(s: StockWaitingSummary | undefined, now: Date): string | null {
  if (!s || s.orders === 0) return null;
  const wait = s.oldestSince ? ` — ${s.orders > 1 ? 'en eskisi ' : ''}${waitingFor(s.oldestSince, now)}` : '';
  const skus = s.skus.length > 0 ? `: ${s.skus.join(', ')}${s.products > s.skus.length ? ', …' : ''}` : '';
  return `${fmt.n(s.orders)} sipariş stok bekliyor${wait}${skus}`;
}

/** Where "Mal kabul" leads for a product: the open shipment that brings it first, else the dock's list. */
export function receivingPath(inbound: readonly StockWaitingInbound[]): string {
  const first = inbound[0];
  return first ? `/kabul/${encodeURIComponent(first.asnNumber)}` : '/kabul';
}

/** "Pazaryerinde 4 adet görünüyor (20.10.2026)": what the seller's store showed when its products were read. */
export const marketplaceText = (seen: MarketplaceStockSeen) => `Pazaryerinde ${fmt.n(seen.qty)} adet görünüyor (${fmt.date(seen.seenAt)})`;

/** One thing to say about a product orders wait for: its tone, and the screen that does it. */
export interface StockWaitingHint {
  readonly tone: 'ok' | 'warn' | 'info';
  readonly text: string;
  readonly link?: { readonly to: string; readonly label: string };
}

/**
 * What the floor can do about a product orders wait for, the most useful first: put away what was
 * received or came back (a unit put away wakes the waiting orders), try again when the stock covers
 * them, look for goods a marketplace shows (they may be in the warehouse without having been
 * entered), receive the shipment that brings them; with no shipment, goods found are received into
 * another open shipment of the seller, else the seller has to announce one.
 */
export function stockWaitingHints(p: StockWaitingProduct): StockWaitingHint[] {
  const hints: StockWaitingHint[] = [];
  if (p.atDock > 0) {
    hints.push({
      tone: 'warn',
      text: `Kabul alanında ${fmt.n(p.atDock)} adet var: Yerleştirme’den rafa koyun, siparişler kendiliğinden ayrılır.`,
      link: { to: '/yerlestirme', label: 'Yerleştirmeye geç' },
    });
  }
  if (p.toShelf > 0) {
    hints.push({
      tone: 'warn',
      text: `İptal ya da iadeden dönen ${fmt.n(p.toShelf)} adet rafa konmayı bekliyor: Yerleştirme → Rafa iade ile rafına koyun, siparişler kendiliğinden ayrılır.`,
      link: { to: '/yerlestirme', label: 'Rafa iadeye geç' },
    });
  }
  if (p.missing === 0) {
    hints.push({ tone: 'ok', text: 'Stok siparişlere yetiyor görünüyor: vardiya sorumlusu “Yeniden dene” ile ayırabilir.' });
  }
  if (p.marketplace) {
    hints.push({ tone: 'warn', text: `${marketplaceText(p.marketplace)}: ürün depoda olabilir, rafları ve kabul alanını kontrol edin.` });
  }
  for (const s of p.inbound) {
    hints.push({
      tone: 'info',
      text: `${s.asnNumber} ${s.status === 'receiving' ? 'kabulde' : 'yolda'}: ${fmt.n(s.remaining)} adet getiriyor${s.expectedAt ? ` (beklenen ${fmt.date(s.expectedAt)})` : ''}. Gelince mal kabulde okutun.`,
      link: { to: `/kabul/${encodeURIComponent(s.asnNumber)}`, label: 'Gönderiyi aç' },
    });
  }
  if (p.missing > 0 && p.inbound.length === 0) {
    // a product no bin holds cannot be "found" on the map: goods without a shipment enter at the dock
    hints.push({
      tone: 'info',
      text:
        'Yolda gönderi yok: satıcının gönderisi bekleniyor, satıcıya “stok yok” bildirimi gitti. Ürün depodaysa mal kabulde satıcının ' +
        'açık bir gönderisine okutup “Yine de kabul et” deyin; açık gönderisi yoksa satıcı Depoya gönderiler’den gönderi bildirmeli.',
    });
  }
  return hints;
}
